// Central partner configuration client (fetch-once, cached) + eligibility helpers.
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Mirrors backend DEFAULT_PARTNERS['bimini'] — used only if the config fetch fails.
const FALLBACK = {
  bimini: {
    partner_id: 'bimini',
    enabled: true,
    popup_enabled: true,
    name: 'Bimini',
    destination_url: 'https://biminihydrotherapy.com/products/nanojet-eco?ref=NINJA&utm_source=ninjathlete&utm_medium=affiliate&utm_campaign=nanojet-eco',
    guide_url: '/blogs/article/bimini-nanojet-oxygen-therapy-athletes',
    disclosure: 'Bimini is a NinjAthlete affiliate partner. We may earn a commission if you purchase through our links.',
    discount: { active: false, label: '10% off with code NINJA', code: 'NINJA' },
    image: {
      src: '/api/community/image/ninjathlete/articles/recovery-amenities/bimini-nanojet-real.webp',
      alt: 'Bimini NanoJet Eco warm-water nanobubble unit set up beside a standard bathtub',
    },
    feature_copy: {
      eyebrow: 'FEATURED RECOVERY PARTNER',
      headline: 'Meet Bimini NanoJet',
      body: "Explore Bimini's warm-water nanobubble system and see how it could fit your recovery routine.",
      cta_primary: 'Explore Bimini',
      cta_secondary: 'Read our Bimini guide',
    },
    popup_copy: {
      eyebrow: 'NINJATHLETE × BIMINI',
      heading: 'Meet your next recovery ritual',
      body: 'Get to know Bimini NanoJet, a warm-water nanobubble system. Explore the product, read our guide, and decide whether it fits your routine.',
      cta_primary: 'Explore Bimini',
      cta_secondary: 'Read the guide',
      dismiss: 'Maybe later',
    },
    eligibility: {
      categories: ['Recovery'],
      tags: ['recovery', 'cold plunge', 'cold-plunge', 'hydrotherapy', 'hbot', 'sauna', 'contrast therapy', 'nanobubble', 'cryotherapy', 'ice bath', 'hyperbaric'],
      excluded_slugs: ['bimini-nanojet-oxygen-therapy-athletes', 'recovery-amenities-gym-economics'],
      feature_excluded_slugs: [],
      feature_included_slugs: ['bimini-nanojet-oxygen-therapy-athletes'],
      included_slugs: [],
      locator_therapies: ['cold-plunge', 'contrast-therapy', 'hyperbaric-oxygen', 'hbot', 'compression-therapy', 'flotation-therapy'],
    },
    experiment_version: 'v1',
  },
};

const cache = {};
const pending = {};

export function getPartnerConfig(partnerId = 'bimini') {
  if (cache[partnerId]) return Promise.resolve(cache[partnerId]);
  if (!pending[partnerId]) {
    pending[partnerId] = fetch(`${API}/api/partners/${partnerId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => { cache[partnerId] = cfg || FALLBACK[partnerId]; return cache[partnerId]; })
      .catch(() => { cache[partnerId] = FALLBACK[partnerId]; return cache[partnerId]; });
  }
  return pending[partnerId];
}

export function usePartnerConfig(partnerId = 'bimini') {
  const [cfg, setCfg] = useState(cache[partnerId] || null);
  useEffect(() => {
    let on = true;
    getPartnerConfig(partnerId).then((c) => { if (on) setCfg(c); });
    return () => { on = false; };
  }, [partnerId]);
  return cfg;
}

export function isArticleEligibleForPartner(article, cfg) {
  if (!cfg?.enabled || !article) return false;
  const el = cfg.eligibility || {};
  const slug = article.slug || '';
  if ((el.excluded_slugs || []).includes(slug)) return false;
  if ((el.included_slugs || []).includes(slug)) return true;
  if ((el.categories || []).includes(article.category)) return true;
  const tags = (article.tags || []).map((t) => (t || '').toLowerCase());
  return (el.tags || []).some((t) => tags.includes(t.toLowerCase()));
}

// Feature-card eligibility is split from popup eligibility: the featured
// partner card appears across ALL recovery content (feature_excluded_slugs,
// default none), while excluded_slugs keeps suppressing the awareness popup
// on pages with their own deliberate lead magnet / partner CTAs.
export function isArticleEligibleForPartnerFeature(article, cfg) {
  if (!cfg?.enabled || !article) return false;
  const el = cfg.eligibility || {};
  const slug = article.slug || '';
  if ((el.feature_excluded_slugs || []).includes(slug)) return false;
  if ((el.feature_included_slugs || []).includes(slug)) return true;
  if ((el.included_slugs || []).includes(slug)) return true;
  if ((el.categories || []).includes(article.category)) return true;
  const tags = (article.tags || []).map((t) => (t || '').toLowerCase());
  return (el.tags || []).some((t) => tags.includes(t.toLowerCase()));
}

// Storage that never throws (private mode / blocked storage must not crash UX).
export const safeStorage = {
  mem: {},
  get(key, session = false) {
    try { return (session ? sessionStorage : localStorage).getItem(key); }
    catch { return this.mem[key] ?? null; }
  },
  set(key, value, session = false) {
    try { (session ? sessionStorage : localStorage).setItem(key, value); }
    catch { this.mem[key] = value; }
  },
};
